import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { LuChevronLeft } from 'react-icons/lu';

import { ClientDatabase, Mode } from '~/types/client';

import cockpit from '../Animator.module.css';
import { AiQuizComposer, AiQuizError, AiQuizReady, AiQuizWriting } from './AiQuiz';
import { AiRequest, useQuizGeneration } from './useQuizGeneration';

/**
 * Quiz express par IA : une phrase → Max écrit le quiz, qui est aussitôt
 * enregistré dans « Mes quiz » et peut être mis à l'affiche sur la TV.
 * La relecture est facultative (décision Victorien 23/09 : « jouer direct »).
 * Arrive depuis le studio avec la phrase déjà saisie (state.request) :
 * la génération démarre alors toute seule.
 */
export function QuizExpress() {
    const navigate = useNavigate();
    const location = useLocation();
    const { clientID } = useParams();
    const { t, i18n } = useTranslation();
    const incoming = (location.state ?? {}) as { request?: AiRequest };

    const ai = useQuizGeneration(clientID, i18n.resolvedLanguage);
    const [draft, setDraft] = useState<AiRequest | undefined>(incoming.request);
    const [composerKey, setComposerKey] = useState(0);
    const [armedId, setArmedId] = useState<string | null>(null);
    const [notRunning, setNotRunning] = useState(false);

    const started = useRef(false);
    useEffect(() => {
        if (started.current || !incoming.request) return;
        started.current = true;
        ai.generate(incoming.request);
        // La demande est consommée : un retour arrière ou un F5 sur cette page ne
        // doit pas relancer une génération (et enregistrer un 2e quiz).
        navigate(location.pathname, { replace: true, state: null });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const backToComposer = (request: AiRequest) => {
        setDraft(request);
        setComposerKey((k) => k + 1);
        ai.reset();
    };

    const { state } = ai;

    return (
        <div className={cockpit.page}>
            <div className={cockpit.shell}>
                <header className={cockpit.header}>
                    <button
                        type="button"
                        className={cockpit.back}
                        aria-label="Retour au studio"
                        onClick={() => navigate(`/animator/${clientID}/quizzes`)}
                    >
                        <LuChevronLeft />
                    </button>
                    <div className={cockpit.headTitles}>
                        <span className={cockpit.headKicker}>Studio Quiz</span>
                        <h1 className={cockpit.title}>
                            {state.status === 'ready' ? 'Votre quiz est prêt' : 'Créer un quiz'}
                        </h1>
                    </div>
                </header>

                {state.status === 'idle' && <AiQuizComposer key={composerKey} initial={draft} onSubmit={ai.generate} />}
                {state.status === 'writing' && (
                    <AiQuizWriting
                        prompt={state.request.prompt}
                        progress={state.progress}
                        onCancel={() => backToComposer(state.request)}
                    />
                )}
                {state.status === 'error' && (
                    <AiQuizError
                        code={state.code}
                        detail={state.detail}
                        onRetry={ai.retry}
                        onBack={() => backToComposer(state.request)}
                        onAddSource={
                            state.request.source ? undefined : () => backToComposer({ ...state.request, source: '' })
                        }
                    />
                )}
                {state.status === 'ready' && (
                    <AiQuizReady
                        showPreview
                        quiz={state.quiz}
                        armed={armedId === state.quiz.id}
                        armNote={notRunning ? t('animator.unquiz.ai.notRunning') : undefined}
                        onArm={() => {
                            if (!clientID) return;
                            const { quiz } = state;
                            ClientDatabase.armUnquizQuiz(clientID, Mode.Anim, quiz.id, quiz.title)
                                .then((ok) => {
                                    setNotRunning(!ok);
                                    if (ok) setArmedId(quiz.id);
                                })
                                .catch(() => {
                                    /* l'animateur peut réessayer, ou l'armer depuis le cockpit */
                                });
                        }}
                        onReview={() => navigate(`/animator/${clientID}/quizzes/${state.quiz.id}`)}
                        onRewrite={() => backToComposer(state.request)}
                    />
                )}
            </div>
        </div>
    );
}
